import {setUser} from '@sentry/browser'
import {useSession} from 'next-auth/react'
import {createContext, FC, PropsWithChildren, useContext, useEffect} from 'react'

import {useUser} from '@/utils/data-fetchers/useUser'
import type {UserForUserFragment} from '@/utils/graphqlSdk'
import {analytics} from '@/utils/tracking/analytics'

type UserDataContextType = {
  user: UserForUserFragment | null
  isLoading: boolean
  isError: boolean
  isLoggedIn: boolean
  // True while we don't know yet if the user is logged in.
  // So the header can show a placeholder, instead of assuming the user is logged out.
  isAuthPending: boolean
}

const UserDataContext = createContext<UserDataContextType>({
  user: null,
  isLoading: false,
  isError: false,
  isLoggedIn: false,
  isAuthPending: false,
})

export const useUserData = (): UserDataContextType => useContext(UserDataContext)

export const UserDataProvider: FC<PropsWithChildren<unknown>> = ({children}) => {
  const {data: session, status} = useSession()

  const userId = session?.user?.id ?? null
  const {data, isLoading, isError} = useUser(userId)

  // The browser loads the session, so it's unknown at first.
  // We also wait for the profile, so we never show a half-loaded user.
  const isAuthPending = status === 'loading' || (Boolean(userId) && isLoading)

  const email = data?.email
  const firstName = data?.profile?.firstname
  const lastName = data?.profile?.lastname

  useEffect(() => {
    if (userId && email) {
      setUser({id: userId, email})
      analytics.identify(userId, {email, firstName, lastName})
    } else {
      setUser(null)
    }
  }, [userId, email, firstName, lastName])

  return (
    <UserDataContext.Provider
      value={{user: data ?? null, isLoggedIn: Boolean(userId), isLoading, isError, isAuthPending}}
    >
      {children}
    </UserDataContext.Provider>
  )
}
